import { useMemo } from 'react';
import { parseISO, isBefore, differenceInDays, getDayOfYear, getYear } from 'date-fns';
import { WWWCompanyDocument, WWWCompanyFinancial } from '../../common/documents/WWWCompanyDocument';

interface ProcessedFinancialData extends Omit<WWWCompanyFinancial, 'accountingPeriodStart' | 'accountingPeriodEnd'> {
  year: number;
}

/** Get the year that the majority of a date range falls on */
const caluclateMajorityYear = (a: string, b: string) => {
  const start = parseISO(a);
  const end = parseISO(b);
  const durationInDays = differenceInDays(end, start);

  const excessDaysInOneYearPeriod = Math.max(0, durationInDays - 365);

  // We MUST ignore everything that goes over 365 days here, because it
  // would mest up the calculations below. If the accounting period crosses
  // the 365 day line, it will be a "special extension" period anyway, so
  // it "should not" affect any calculations...
  const endDayOfYear = getDayOfYear(end) - excessDaysInOneYearPeriod;
  const halfOfDurationInDays = Math.floor(Math.min(durationInDays, 365) / 2);

  // If half of the duration is larger than the (day of year) the period ended on,
  // most of the prediod MUST land on the year the period started on
  if (halfOfDurationInDays > endDayOfYear) {
    return getYear(start);
  } else {
    return getYear(end);
  }
};

const calculateAccountingPeriods = (financials: WWWCompanyFinancial[]) => {
  // Safeguard for invalid financial data
  if (!financials || !financials.length) return [];

  // Order the financial data by date
  const orderedFinancials = [...financials].sort((a, b) =>
    isBefore(parseISO(a.accountingPeriodEnd), parseISO(b.accountingPeriodEnd)) ? 1 : -1
  );

  // Get the year for the latest period
  const latestPediod = orderedFinancials[0];
  const latestPeriodYear = caluclateMajorityYear(latestPediod.accountingPeriodStart, latestPediod.accountingPeriodEnd);

  // Every period after the first will be in sequential year order
  // TODO: Can an accounting period ever skip a year?
  const processedFinancialData = orderedFinancials.map<ProcessedFinancialData>(
    ({ accountingPeriodStart, accountingPeriodEnd, ...rest }, index) => ({
      ...rest,
      year: latestPeriodYear - index,
    })
  );

  return processedFinancialData;
};

const calculateAbsoluteScale = (low: number, high: number): [number, number] => {
  const padding = high * 0.2;
  return [0, high + padding];
};

const calculateRelativeScale = (low: number, high: number): [number, number] => {
  const padding = (high - low) * 0.2;
  return [low - padding, high + padding];
};

/** Calculate scale values (high & low) for a graph */
const calculateScale = (points: number[]) => {
  const low = Math.min(...points);
  const high = Math.max(...points);

  // Only render with absolute scale (0 - hight) if graph is diverse enough
  const isAbsolutePossible = Math.abs((high - low) / low) > 0.2 && low > 0;
  if (isAbsolutePossible) return calculateAbsoluteScale(low, high);

  return calculateRelativeScale(low, high);
};

export const useFinancialData = (company: WWWCompanyDocument) => {
  const financials = useMemo(() => calculateAccountingPeriods(company.financials), [company.financials]);

  const turnover = useMemo(() => {
    const turnoverData = financials.map(({ year, turnover }) => ({
      year,
      value: turnover,
    }));

    const turnoverChange = financials[0].turnoverChangePercentage || 0;

    const turnoverScale = calculateScale(turnoverData.map(({ value }) => value));

    return { turnoverData, turnoverChange, turnoverScale };
  }, [financials]);

  const operating = useMemo(() => {
    const operatingData = financials.map(({ year, operatingResultPercentage, turnover }) => ({
      year,
      value: Math.round((operatingResultPercentage * turnover) / 100000) * 1000,
    }));

    let operatingChange: number = 0;
    if (financials.length >= 2) {
      const one = operatingData[1].value;
      const two = operatingData[0].value;

      operatingChange = ((two - one) / Math.abs(one)) * 100;
    }

    const operatingScale = calculateScale(operatingData.map(({ value }) => value));

    return { operatingData, operatingChange, operatingScale };
  }, [financials]);

  const equity = useMemo(() => {
    const equityData = financials.map(({ year, equityRatio }) => ({
      year,
      value: equityRatio,
    }));

    let equityChange: number = 0;
    if (financials.length >= 2) {
      const one = equityData[1].value;
      const two = equityData[0].value;

      equityChange = (two - one) * 100;
    }

    return { equityData, equityChange };
  }, [financials]);

  const employee = useMemo(() => {
    const employeeData = financials.map(({ year, numberOfEmployees }) => ({
      year,
      value: numberOfEmployees,
    }));

    let employeeChange: number = 0;
    if (financials.length >= 2) {
      const one = employeeData[1].value;
      const two = employeeData[0].value;
      employeeChange = ((two - one) / Math.abs(one)) * 100;
    }

    return { employeeData, employeeChange };
  }, [financials]);

  return {
    ...turnover,
    ...operating,
    ...equity,
    ...employee,
  };
};
